import React from "react";
import { twMerge } from "tailwind-merge";

export type Column<Row> = {
  /** Unique among the table's columns. */
  key: string;
  label: React.ReactNode;
  render: (row: Row) => React.ReactNode;
};

/**
 * A plain data table. Name it with `aria-labelledby` or `aria-label`; rows
 * that need an action carry a button in a cell.
 */
function Table<Row>({
  columns,
  rows,
  rowKey,
  empty,
  className,
  ...props
}: {
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** Fills the body when there are no rows. */
  empty: React.ReactNode;
} & React.TableHTMLAttributes<HTMLTableElement>) {
  return (
    <div
      className={twMerge(
        "relative overflow-x-auto rounded-lg border border-gray-200",
        className,
      )}
    >
      <table className="min-w-full text-sm" {...props}>
        <thead className="bg-gray-50">
          <tr>
            {columns.map(column => (
              <th
                key={column.key}
                scope="col"
                className="whitespace-nowrap px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-gray-500"
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 border-t border-gray-200">
          {rows.length ? (
            rows.map(row => (
              <tr key={rowKey(row)}>
                {columns.map(column => (
                  <td key={column.key} className="px-4 py-3 text-gray-900">
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-6 text-center text-gray-400"
              >
                {empty}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
